import { Show, createEffect, createResource, createSignal } from "solid-js";
import { ClassesPage } from "~/features/classes/ClassesPage";
import type { ClassTeamModule } from "~/features/classes/classTeam";
import type { ClassesModule, TeacherClass } from "~/features/classes/classes";
import { Icon } from "~/ui/Icon";
import type { SettingsModule, TeacherProfile } from "./settings";

type Tab = "profile" | "classes";
type SaveState = "idle" | "loading" | "success";

function SaveButton(props: { state: SaveState; onClick: () => void }) {
  return (
    <button class="btn btn-primary" type="button" disabled={props.state === "loading"} onClick={props.onClick} style={{ background: props.state === "success" ? "#22c55e" : undefined, "border-color": props.state === "success" ? "#16a34a" : undefined }}>
      <Show when={props.state === "success"} fallback={<><Icon name="save" style={{ width: "16px", height: "16px" }} /> {props.state === "loading" ? "Wird gespeichert…" : "Speichern"}</>}>
        <Icon name="checkmark" style={{ width: "16px", height: "16px" }} /> Gespeichert!
      </Show>
    </button>
  );
}

export function SettingsPage(props: {
  module: SettingsModule;
  classesModule: ClassesModule;
  teamModule?: ClassTeamModule;
  classes: TeacherClass[];
  onClassesReload: () => Promise<TeacherClass[]>;
  onClassSelect: (value: TeacherClass) => void;
  onClassRemember: (value: TeacherClass | undefined) => void;
  onProfileSaved: (profile: TeacherProfile) => void;
  onPasswordChanged: () => void;
}) {
  const [tab, setTab] = createSignal<Tab>("profile");
  const [profile] = createResource(() => props.module.load());
  const [firstName, setFirstName] = createSignal("");
  const [lastName, setLastName] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [schoolName, setSchoolName] = createSignal("");
  const [profileState, setProfileState] = createSignal<SaveState>("idle");
  const [passwordState, setPasswordState] = createSignal<SaveState>("idle");
  const [toast, setToast] = createSignal<{ type: "success" | "error"; text: string }>();
  const [currentPassword, setCurrentPassword] = createSignal("");
  const [newPassword, setNewPassword] = createSignal("");
  const [confirmPassword, setConfirmPassword] = createSignal("");
  const [showCurrent, setShowCurrent] = createSignal(false);
  const [showNew, setShowNew] = createSignal(false);
  const [showConfirm, setShowConfirm] = createSignal(false);

  createEffect(() => {
    const value = profile();
    if (!value) return;
    setFirstName(value.firstName);
    setLastName(value.lastName);
    setEmail(value.email);
    setSchoolName(value.schoolName);
  });

  const notify = (type: "success" | "error", text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(undefined), 3000);
  };

  const saveProfile = async () => {
    setProfileState("loading");
    try {
      const saved = await props.module.saveProfile({
        firstName: firstName(),
        lastName: lastName(),
        email: email(),
        schoolName: schoolName()
      });
      props.onProfileSaved(saved);
      setProfileState("success");
      window.setTimeout(() => setProfileState("idle"), 2500);
    } catch (cause) {
      notify("error", cause instanceof Error ? cause.message : "Fehler beim Speichern.");
      setProfileState("idle");
    }
  };

  const savePassword = async () => {
    setPasswordState("loading");
    try {
      await props.module.changePassword({ currentPassword: currentPassword(), newPassword: newPassword(), confirmation: confirmPassword() });
      props.onPasswordChanged();
    } catch (cause) {
      notify("error", cause instanceof Error ? cause.message : "Fehler beim Ändern.");
      setPasswordState("idle");
    }
  };

  const tabStyle = (value: Tab) => ({
    padding: "8px 20px",
    border: "none",
    background: "none",
    cursor: "pointer",
    "font-size": "0.95rem",
    "font-weight": 600,
    "border-bottom": `3px solid ${tab() === value ? "var(--primary)" : "transparent"}`,
    color: tab() === value ? "var(--primary)" : "var(--text-muted)",
    "margin-bottom": "-2px",
    transition: "color 0.15s"
  });

  return (
    <>
      <Show when={toast()}>{(message) => <div class="toast-container"><div class={`toast ${message().type}`} role={message().type === "error" ? "alert" : "status"}>{message().text}</div></div>}</Show>
      <div style={{ "margin-bottom": "20px" }}><h1>Einstellungen</h1></div>
      <div style={{ display: "flex", gap: "4px", "margin-bottom": "24px", "border-bottom": "2px solid var(--border)" }}>
        <button type="button" style={tabStyle("profile")} onClick={() => setTab("profile")}>⚙️ Profil</button>
        <button type="button" style={tabStyle("classes")} onClick={() => setTab("classes")}><Icon name="book" style={{ width: "16px", height: "16px" }} /> Klassen</button>
      </div>

      <Show when={!profile.loading} fallback={<div class="loading-spinner" style={{ margin: "60px auto" }} />}>
        <Show when={tab() === "profile"} fallback={
          <ClassesPage settingsMode classes={props.classes} module={props.classesModule} teamModule={props.teamModule} onReload={props.onClassesReload} onSelect={props.onClassSelect} onRemember={props.onClassRemember} />
        }>
          <div class="card" style={{ "margin-bottom": "20px" }}>
            <h2 style={{ "font-size": "1rem", "font-weight": 700, "margin-bottom": "16px" }}><Icon name="person" style={{ width: "16px", height: "16px" }} /> Persönliche Daten</h2>
            <div style={{ display: "grid", "grid-template-columns": "1fr 1fr", gap: "12px", "margin-bottom": "12px" }}>
              <div class="form-group"><label class="form-label" for="settings-first-name">Vorname</label><input id="settings-first-name" class="form-input" value={firstName()} onInput={(event) => setFirstName(event.currentTarget.value)} /></div>
              <div class="form-group"><label class="form-label" for="settings-last-name">Nachname</label><input id="settings-last-name" class="form-input" value={lastName()} onInput={(event) => setLastName(event.currentTarget.value)} /></div>
            </div>
            <div class="form-group" style={{ "margin-bottom": "16px" }}><label class="form-label" for="settings-email">E-Mail-Adresse</label><input id="settings-email" type="email" class="form-input" value={email()} onInput={(event) => setEmail(event.currentTarget.value)} /></div>
            <SaveButton state={profileState()} onClick={saveProfile} />
          </div>

          <div class="card" style={{ "margin-bottom": "20px" }}>
            <h2 style={{ "font-size": "1rem", "font-weight": 700, "margin-bottom": "4px" }}><Icon name="school" style={{ width: "16px", height: "16px" }} /> Schulname</h2>
            <p style={{ "font-size": "0.85rem", color: "var(--text-muted)", "margin-bottom": "16px" }}>Wird in allen Klassen und Berichten angezeigt.</p>
            <div class="form-group" style={{ "margin-bottom": "16px" }}><label class="form-label" for="settings-school">Schulname</label><input id="settings-school" class="form-input" value={schoolName()} onInput={(event) => setSchoolName(event.currentTarget.value)} placeholder="z.B. Gesamtschule Elsen" maxlength={200} /></div>
            <SaveButton state={profileState()} onClick={saveProfile} />
          </div>

          <div class="card">
            <h2 style={{ "font-size": "1rem", "font-weight": 700, "margin-bottom": "4px" }}><Icon name="lock" style={{ width: "16px", height: "16px" }} /> Passwort ändern</h2>
            <p style={{ "font-size": "0.8rem", color: "var(--text-muted)", "margin-bottom": "16px" }}>Passwörter werden verschlüsselt gespeichert und können nicht angezeigt werden. Gib dein aktuelles Passwort ein um ein neues zu setzen.</p>
            <PasswordField label="Aktuelles Passwort" value={currentPassword()} visible={showCurrent()} onInput={setCurrentPassword} onToggle={() => setShowCurrent(!showCurrent())} />
            <PasswordField label="Neues Passwort" value={newPassword()} visible={showNew()} onInput={setNewPassword} onToggle={() => setShowNew(!showNew())} placeholder="Mindestens 8 Zeichen" />
            <PasswordField label="Neues Passwort bestätigen" value={confirmPassword()} visible={showConfirm()} onInput={setConfirmPassword} onToggle={() => setShowConfirm(!showConfirm())} placeholder="Wiederholen" />
            <button class="btn btn-primary" type="button" disabled={passwordState() === "loading"} onClick={savePassword}><Icon name={passwordState() === "success" ? "checkmark" : "lock"} style={{ width: "16px", height: "16px" }} /> {passwordState() === "loading" ? "Wird gespeichert…" : passwordState() === "success" ? "Gespeichert!" : "Passwort ändern"}</button>
          </div>
        </Show>
      </Show>
    </>
  );
}

function PasswordField(props: { label: string; value: string; visible: boolean; placeholder?: string; onInput: (value: string) => void; onToggle: () => void }) {
  return (
    <div class="form-group" style={{ "margin-bottom": "12px" }}>
      <label class="form-label">{props.label}</label>
      <div style={{ position: "relative" }}>
        <input type={props.visible ? "text" : "password"} class="form-input" value={props.value} onInput={(event) => props.onInput(event.currentTarget.value)} placeholder={props.placeholder ?? "••••••••"} style={{ "padding-right": "44px" }} />
        <button type="button" onClick={props.onToggle} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: "2px", opacity: props.visible ? 1 : 0.5 }} title={props.visible ? "Passwort verbergen" : "Passwort anzeigen"}><Icon name="eye" style={{ width: "16px", height: "16px" }} /></button>
      </div>
    </div>
  );
}
